<template>
  <div :class="rootClass">
    <div v-if="!headerOnly" class="iq-card-header pt-2"><div class="iq-card-title"><h4 class="card-title">{{ group.label }}</h4></div></div>
    <div class="iq-card-body">
      <p v-if="!headerOnly && !group.rows.length" class="metric-group__empty">{{ emptyText }}</p>
      <div v-else class="metric-group__scroll">
        <table class="metric-group__table">
          <!-- Lebar kolom dipatok biar garis pemisahnya sejajar di semua kartu. -->
          <colgroup>
            <col class="metric-group__col-metric" />
            <col class="metric-group__col-value" />
            <template v-for="cmp in comparisonColumns">
              <col :key="`${cmp.col.letter}-value`" class="metric-group__col-value" />
              <col :key="`${cmp.col.letter}-delta`" class="metric-group__col-delta" />
            </template>
            <col />
          </colgroup>
          <!-- Header dipasang sekali di atas semua kartu; kartu biasa cuma isi baris. -->
          <thead v-if="headerOnly">
            <tr>
              <th class="metric-group__metric"></th>
              <th class="metric-group__side">
                <div class="metric-group__legend">
                  <span class="metric-group__letter">{{ baseColumn.letter }}</span>
                  <span class="metric-group__name">{{ baseColumn.name }}</span>
                  <div class="metric-group__range">
                    <span v-for="(part, i) in rangeParts(baseColumn.rangeText)" :key="i" class="metric-group__range-part">{{ part }}</span>
                  </div>
                  <span class="metric-group__base-tag">Base</span>
                  <span v-if="statusBadge(baseColumn.status)" class="badge badge-secondary">{{ statusBadge(baseColumn.status) }}</span>
                </div>
              </th>
              <th v-for="cmp in comparisonColumns" :key="cmp.col.letter" colspan="2" class="metric-group__side metric-group__side--cmp">
                <div class="metric-group__legend">
                  <span class="metric-group__letter">{{ cmp.col.letter }}</span>
                  <span class="metric-group__name">{{ cmp.col.name }}</span>
                  <div class="metric-group__range">
                    <span v-for="(part, i) in rangeParts(cmp.col.rangeText)" :key="i" class="metric-group__range-part">{{ part }}</span>
                  </div>
                  <span v-if="statusBadge(cmp.col.status)" class="badge badge-secondary">{{ statusBadge(cmp.col.status) }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody v-else>
            <template v-for="item in bodyItems">
              <!-- sel kosong tetap dirender biar garis pemisahnya gak putus -->
              <tr v-if="item.heading" :key="item.key" class="metric-group__subhead">
                <td class="metric-group__metric">{{ item.heading }}</td>
                <td class="metric-group__value metric-group__value--base"></td>
                <template v-for="cmp in comparisonColumns">
                  <td :key="`${cmp.col.letter}-value`" class="metric-group__value metric-group__value--cmp"></td>
                  <td :key="`${cmp.col.letter}-delta`" class="metric-group__delta"></td>
                </template>
              </tr>
              <tr v-else :key="item.key">
                <td :class="['metric-group__metric', { 'metric-group__metric--nested': item.row.subgroup }]">
                  {{ item.row.label }}<span v-if="item.row.unit" class="metric-group__unit"> ({{ item.row.unit }})</span>
                </td>
                <td
                  class="metric-group__value metric-group__value--base"
                  :data-letter="baseColumn.letter"
                  :title="fullCell(item.row, 0)"
                >{{ cell(item.row, 0) }}</td>
                <template v-for="cmp in comparisonColumns">
                  <td
                    :key="`${cmp.col.letter}-value`"
                    class="metric-group__value metric-group__value--cmp"
                    :data-letter="cmp.col.letter"
                    :title="fullCell(item.row, cmp.index)"
                  >{{ cell(item.row, cmp.index) }}</td>
                  <td :key="`${cmp.col.letter}-delta`" class="metric-group__delta">
                    {{ deltaFor(item.row, cmp.index) }}<span v-if="flagged(item.row, cmp.index)" class="metric-group__flag" :title="warnText">!</span>
                  </td>
                </template>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { formatNum } from '@/util/number';
import { toDuration } from '@/util/date';
import { deltaText, shortDuration } from '@/util/summaryCompareModel';

const STATUS_LABELS = {
  noDevices: 'No devices',
  failed: 'Failed',
};

export default {
  name: 'MetricGroupCard',
  props: {
    group: { type: Object, default: null },
    headerOnly: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
    columns: { type: Array, required: true },
    warnFlags: { type: Array, default: () => [] },
    warnText: { type: String, default: '' },
    emptyText: { type: String, default: '' },
  },
  computed: {
    columnCount() {
      return (this.columns || []).length;
    },
    rootClass() {
      return [
        'iq-card',
        'metric-group',
        `metric-group--cols-${this.columnCount}`,
        { 'metric-group--header': this.headerOnly, 'metric-group--stacked': this.stacked && this.columnCount > 1 },
      ];
    },
    baseColumn() {
      return (this.columns && this.columns[0]) || {};
    },
    // Kolom selain Base, dibawa bareng index aslinya buat akses values[]/pcts[]/warnFlags[].
    comparisonColumns() {
      return (this.columns || []).slice(1).map((col, i) => ({ col, index: i + 1 }));
    },
    // Sisipin sub-judul tiap kali subgroup ganti (mis. PORT, STARBOARD di Flowmeter).
    bodyItems() {
      if (!this.group) return [];
      const items = [];
      let current = '';
      this.group.rows.forEach((row) => {
        const subgroup = row.subgroup || '';
        if (subgroup && subgroup !== current) items.push({ heading: subgroup, key: `subgroup-${subgroup}` });
        current = subgroup;
        items.push({ row, key: row.key });
      });
      return items;
    },
  },
  methods: {
    cell(row, i) {
      const value = row.values[i];
      if (value === null) return '—';
      return row.format === 'duration' ? shortDuration(value) : formatNum(value, 2);
    },
    // Durasi lengkap cuma di tooltip; angka biasa gak butuh.
    fullCell(row, i) {
      const value = row.values[i];
      return row.format === 'duration' && value !== null ? toDuration(value) : null;
    },
    deltaFor(row, i) {
      return deltaText(row.pcts[i]);
    },
    flagged(row, i) {
      return !!(this.warnFlags[i] && row.measure === 'total' && row.pcts[i] !== null);
    },
    // Patahnya cuma boleh di pisah rentang, bukan di tengah tanggal.
    rangeParts(text) {
      const [start, end] = String(text || '').split(' – ');
      return end === undefined ? [start] : [`${start} –`, end];
    },
    statusBadge(status) {
      return STATUS_LABELS[status] || '';
    },
  },
};
</script>

<style scoped>
.metric-group__empty {
  margin: 0;
  font-size: 0.8125rem;
  color: #6c757d;
}
.metric-group__scroll {
  overflow-x: auto;
}
.metric-group__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
/* label & angka lebar tetap dempet di kiri; kolom terakhir (tanpa lebar) nyerap sisa lebar kartu */
.metric-group__col-metric { width: 280px; }
.metric-group__col-value { width: 130px; }
.metric-group__col-delta { width: 100px; }
.metric-group__table th,
.metric-group__table td {
  padding: 5px 10px;
  vertical-align: middle;
}
/* nama & rentang antar kolom sejajar walau tinggi header beda */
.metric-group__table thead th {
  vertical-align: bottom;
}
.metric-group__metric {
  text-align: left;
  overflow-wrap: anywhere;
}
.metric-group__subhead td {
  padding-top: 10px;
  padding-bottom: 2px;
}
.metric-group__subhead .metric-group__metric {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.02em;
}
.metric-group__table .metric-group__metric--nested {
  padding-left: 22px;
}
.metric-group__unit {
  color: #6c757d;
  font-size: 0.8125rem;
}
.metric-group__side {
  text-align: right;
}
.metric-group__side--cmp {
  border-left: 1px solid #dee2e6;
}
.metric-group__letter {
  display: inline-block;
  min-width: 1em;
  margin-right: 4px;
  color: #adb5bd;
  font-weight: 700;
}
.metric-group__base-tag {
  margin-left: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
/* flex biar urutan DOM (huruf,nama,rentang,tag) bisa dipentaskan ulang: rentang turun ke barisnya sendiri */
.metric-group__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: baseline;
}
.metric-group__base-tag,
.metric-group__legend > .badge {
  order: 1;
}
/* flex buang spasi teks antar item, gantinya margin eksplisit */
.metric-group__legend > .badge {
  margin-left: 6px;
}
.metric-group__name {
  font-weight: 600;
}
.metric-group__range {
  order: 2;
  flex-basis: 100%;
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: normal;
}
/* header desktop: mulai & akhir selalu dua baris biar nama kapal semua kolom sejajar */
.metric-group__range-part {
  display: block;
  white-space: nowrap;
}
/* legenda bertumpuk tetap satu baris mengalir */
.metric-group--stacked .metric-group__range-part {
  display: inline;
}
.metric-group--stacked .metric-group__range-part + .metric-group__range-part {
  margin-left: 0.25em;
}
.metric-group__value,
.metric-group__delta {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.metric-group__value--cmp {
  border-left: 1px solid #dee2e6;
}
/* delta ditebalkan biar beda kelihatan dari angka biasa, warnanya tetap netral */
.metric-group__delta {
  font-weight: 600;
}
.metric-group__flag {
  margin-left: 4px;
  color: #ffc107;
  cursor: help;
}
/* judul grup dikecilin biar antar grup gak kepisah jauh */
.metric-group {
  margin-bottom: 10px;
}
.metric-group .card-title {
  font-size: 1rem;
  margin-bottom: 0;
}
.metric-group .iq-card-body {
  padding-top: 6px;
}
.metric-group--header {
  margin-top: 6px;
  margin-bottom: 0;
}
.metric-group--header .iq-card-body {
  padding-top: 6px;
  padding-bottom: 6px;
}

@media (max-width: 767px) {
  .metric-group .iq-card-body {
    padding: 8px 12px 12px;
  }
  .metric-group--header .iq-card-body {
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

/* cols-1: gak pernah bertumpuk, cuma label yang gak dipatok lebar di layar sempit */
@media (max-width: 767px) {
  .metric-group--cols-1 .metric-group__table {
    table-layout: auto;
  }
  .metric-group--cols-1 .metric-group__col-metric {
    width: auto;
  }
}

/* mode bertumpuk: dipicu prop stacked (halaman yang ukur lebar konten), bukan viewport */
.metric-group--stacked .metric-group__table {
  table-layout: auto;
}
.metric-group--stacked .metric-group__table colgroup {
  display: none;
}
.metric-group--stacked .metric-group__table th,
.metric-group--stacked .metric-group__table td {
  padding: 4px 8px;
}
/* header jadi legenda: satu th per kolom, satu baris penuh */
.metric-group--stacked .metric-group__table thead tr {
  display: block;
}
.metric-group--stacked .metric-group__table thead th {
  display: block;
  width: 100%;
  text-align: left;
  border-left: none !important;
}
.metric-group--stacked .metric-group__table thead .metric-group__metric {
  display: none;
}
/* legenda: balik ke alur blok biasa biar urutan DOM (huruf,nama,rentang,tag) jadi satu baris mengalir */
.metric-group--stacked .metric-group__legend {
  display: block;
  font-size: 0.8125rem;
}
.metric-group--stacked .metric-group__range {
  display: inline;
  margin-left: 0.35em;
  font-size: 0.6875rem;
}
/* baris data: huruf+angka 170px | delta 100px, rapat di kiri biar mata gak nyebrang selebar kartu */
.metric-group--stacked .metric-group__table tbody tr {
  display: grid;
  grid-template-columns: 170px 100px;
  justify-content: start;
  column-gap: 8px;
}
.metric-group--stacked .metric-group__table tbody tr + tr {
  border-top: 1px solid #f1f3f5;
}
.metric-group--stacked .metric-group__table tbody .metric-group__metric,
.metric-group--stacked .metric-group__table tbody .metric-group__value--base {
  grid-column: 1 / -1;
}
.metric-group--stacked .metric-group__table tbody .metric-group__metric {
  padding-top: 8px;
  padding-bottom: 0;
  white-space: normal;
}
/* Base tetap selebar kolom angka pembanding biar angkanya segaris */
.metric-group--stacked .metric-group__table tbody .metric-group__value--base {
  justify-self: start;
  width: 170px;
}
/* menang atas padding shorthand di atas biar indentasi nested tetap kelihatan */
.metric-group--stacked .metric-group__table tbody .metric-group__metric--nested {
  padding-left: 22px;
}
.metric-group--stacked .metric-group__table tbody .metric-group__value--base,
.metric-group--stacked .metric-group__table tbody .metric-group__value--cmp {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
  border-left: none;
  font-size: 0.8125rem;
  white-space: normal;
}
.metric-group--stacked .metric-group__table tbody .metric-group__value--base::before,
.metric-group--stacked .metric-group__table tbody .metric-group__value--cmp::before {
  content: attr(data-letter);
  color: #adb5bd;
  font-weight: 600;
  font-size: 0.6875rem;
}
.metric-group--stacked .metric-group__table tbody .metric-group__delta {
  font-size: 0.8125rem;
  white-space: normal;
}
/* sub-judul tetap satu sel penuh */
.metric-group--stacked .metric-group__table tbody .metric-group__subhead {
  display: block;
}
.metric-group--stacked .metric-group__table tbody .metric-group__subhead td:not(:first-child) {
  display: none;
}
.metric-group--stacked .metric-group__table tbody .metric-group__subhead .metric-group__metric {
  padding-top: 12px;
}
.metric-group--stacked .metric-group__table tbody .metric-group__subhead + tr {
  border-top: none;
}
</style>
